// Start the public saved-board read while the rate page's lazy module loads.
// This is a single-use request, not a cache: no quote, identity or browser
// storage is involved, and every later refresh still reaches the server.
interface BoardPreload {
  controller: AbortController;
  payload: Promise<unknown>;
  expiry: ReturnType<typeof setTimeout>;
}

let preload: BoardPreload | undefined;

async function fetchBoardPayload(signal?: AbortSignal): Promise<unknown> {
  try {
    const response = await fetch('/api/consumer/v1/board', {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
      signal,
    });
    return response.ok ? await response.json() : null;
  } catch {
    return null;
  }
}

export function preloadConsumerBoard(): void {
  if (preload) return;
  const controller = new AbortController();
  const request: BoardPreload = {
    controller,
    payload: fetchBoardPayload(controller.signal),
    expiry: setTimeout(() => {
      if (preload === request) preload = undefined;
      controller.abort();
    }, 12_000),
  };
  preload = request;
}

export async function readConsumerBoardPayload(signal?: AbortSignal): Promise<unknown> {
  const request = preload;
  if (!request) return fetchBoardPayload(signal);
  preload = undefined;
  const abort = () => request.controller.abort();
  if (signal?.aborted) abort();
  else signal?.addEventListener('abort', abort, { once: true });
  try {
    const payload = await request.payload;
    return signal?.aborted ? null : payload;
  } finally {
    clearTimeout(request.expiry);
    signal?.removeEventListener('abort', abort);
  }
}
